import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import TempLabel from "./TempLabel";

const COMPACT_COLUMN_WIDTHS = {
  period: 80,
  periodo: 80,

  category: 130,
  categoria: 130,

  course: 210,
  curso: 210,

  code: 100,
  codigo: 100,

  faculty: 145,
  facultad: 145,

  status: 95,
  estado: 95,

  actions: 200,
  acciones: 200,
};

const normalizeColumnId = (columnId) => {
  return String(columnId || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const TableComponent = ({
  columns = [],
  rows = [],
  sortBy = "date",
  asc = false,
  prioritizeButtons = false,
  showTempLabel = false,
  noData = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortedRows, setSortedRows] = useState([...rows]);

  const isDesktop = useMediaQuery("(min-width: 1181px) and (hover: hover) and (pointer: fine)");
  const isNarrowScreen = useMediaQuery("(max-width: 920px)");
  const isTouchPortrait = useMediaQuery("(hover: none) and (pointer: coarse) and (orientation: portrait)");
  const isTouchLandscapeTablet = useMediaQuery(
    "(hover: none) and (pointer: coarse) and (orientation: landscape) and (min-width: 921px) and (max-width: 1366px)",
  );
  const isMediumViewport = useMediaQuery("(min-width: 921px) and (max-width: 1180px)");

  const useCardLayout = !isDesktop && (isNarrowScreen || isTouchPortrait);

  const useCompactTable = !isDesktop && !useCardLayout && (isTouchLandscapeTablet || isMediumViewport);

  const comparator = (a, b) => {
    const isAsc = asc;

    if (prioritizeButtons) {
      const aHasButton = Array.isArray(a.actions) && a.actions.length > 0;
      const bHasButton = Array.isArray(b.actions) && b.actions.length > 0;

      if (aHasButton && !bHasButton) return -1;
      if (!aHasButton && bHasButton) return 1;

      if (sortBy === "date") {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);

        return isAsc ? aDate - bDate : bDate - aDate;
      }
    } else if (sortBy === "date") {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);

      return isAsc ? aDate - bDate : bDate - aDate;
    }

    return 0;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  useEffect(() => {
    setSortedRows([...rows].sort(comparator));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortBy, asc, prioritizeButtons]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [page, rowsPerPage, sortedRows.length]);

  const formatValue = (column, value) => {
    if (column.id === "date" && column.format) {
      return column.format(value);
    }

    return value;
  };

  const renderValue = (column, value) => {
    const formattedValue = formatValue(column, value);

    if (formattedValue === null || formattedValue === undefined || formattedValue === "") {
      return "—";
    }

    return formattedValue;
  };

  const renderColumnLabel = (column) => {
    return showTempLabel ? <TempLabel>{column.label}</TempLabel> : column.label;
  };

  const getCompactColumnWidth = (columnId) => {
    const normalizedId = normalizeColumnId(columnId);

    return COMPACT_COLUMN_WIDTHS[normalizedId] || 120;
  };

  const renderActions = (actions, rowKey, { stacked = false, fullWidth = false } = {}) => {
    if (!Array.isArray(actions) || actions.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : "row",
          flexWrap: "nowrap",
          alignItems: "stretch",
          justifyContent: "flex-start",
          gap: 0.75,
          width: fullWidth ? "100%" : stacked ? "145px" : "max-content",

          maxWidth: "100%",
        }}
      >
        {actions.map((action, actionIndex) => {
          const button = (
            <Button
              variant={action.variant || "contained"}
              color={action.color || "primary"}
              size="small"
              onClick={action.onClick}
              disabled={action.disabled}
              sx={{
                whiteSpace: "nowrap",

                minWidth: fullWidth || stacked ? 0 : "105px",

                width: fullWidth || stacked ? "100%" : "auto",

                ...(useCompactTable && {
                  fontSize: "0.68rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.75,
                }),
              }}
            >
              {action.buttonText}
            </Button>
          );

          if (!action.tooltip) {
            return <React.Fragment key={`button-${rowKey}-${actionIndex}`}>{button}</React.Fragment>;
          }

          return (
            <Tooltip
              key={`tooltip-${rowKey}-${actionIndex}`}
              title={action.tooltip}
              arrow
              placement="top"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: {
                      xs: 240,
                      sm: 260,
                      lg: 220,
                    },
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.78rem",
                      lg: "0.72rem",
                    },
                    lineHeight: 1.25,
                    textAlign: "center",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    px: {
                      xs: 1.25,
                      sm: 1.25,
                      lg: 1,
                    },
                    py: {
                      xs: 0.75,
                      sm: 0.75,
                      lg: 0.5,
                    },
                  },
                },
              }}
            >
              <span
                tabIndex={0}
                style={{
                  display: "inline-flex",
                  width: fullWidth || stacked ? "100%" : "auto",
                }}
              >
                {button}
              </span>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;

    return sortedRows.slice(start, end);
  }, [page, rowsPerPage, sortedRows]);

  const contentColumns = useMemo(() => {
    return columns.filter((column) => column.id !== "actions");
  }, [columns]);

  const primaryColumn = useMemo(() => {
    const preferredColumnIds = ["nombre_curso"];

    return contentColumns.find((column) => preferredColumnIds.includes(normalizeColumnId(column.id))) || contentColumns[0];
  }, [contentColumns]);

  const secondaryColumns = useMemo(() => {
    if (!primaryColumn) return contentColumns;

    return contentColumns.filter((column) => column.id !== primaryColumn.id);
  }, [contentColumns, primaryColumn]);

  const actionsColumn = useMemo(() => {
    return columns.find((column) => column.id === "actions");
  }, [columns]);

  const shouldShowNoData = noData || sortedRows.length === 0;

  return (
    <Paper
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        borderRadius: 1.5,
        backgroundColor: useCardLayout ? "transparent" : "background.paper",
        boxShadow: useCardLayout ? "none" : undefined,
      }}
    >
      {useCardLayout ? (
        <Stack spacing={1.5}>
          {visibleRows.map((row, rowIndex) => {
            const absoluteRowIndex = page * rowsPerPage + rowIndex;

            const rowKey = row.id || row.assignapp_questionnaire_id || absoluteRowIndex;

            const actions = actionsColumn ? row[actionsColumn.id] : row.actions;

            return (
              <Paper
                key={`card-${rowKey}`}
                component="article"
                variant="outlined"
                aria-label={`Cuestionario ${absoluteRowIndex + 1}`}
                sx={{
                  p: {
                    xs: 1.75,
                    sm: 2.25,
                  },
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1.5px solid",
                  borderColor: "grey.400",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                  backgroundColor: absoluteRowIndex % 2 === 0 ? "background.paper" : "grey.50",
                }}
              >
                {primaryColumn && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      {renderColumnLabel(primaryColumn)}
                    </Typography>

                    <Typography
                      component="div"
                      sx={{
                        fontSize: {
                          xs: "0.95rem",
                          sm: "1rem",
                        },
                        fontWeight: 600,
                        lineHeight: 1.35,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {renderValue(primaryColumn, row[primaryColumn.id])}
                    </Typography>
                  </Box>
                )}

                {secondaryColumns.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr)",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        columnGap: 2,
                        rowGap: 1.5,
                      }}
                    >
                      {secondaryColumns.map((column) => (
                        <Box key={`card-field-${rowKey}-${column.id}`} sx={{ minWidth: 0 }}>
                          <Typography
                            component="div"
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mb: 0.25,
                              fontWeight: 600,
                            }}
                          >
                            {renderColumnLabel(column)}
                          </Typography>

                          <Typography
                            component="div"
                            sx={{
                              fontSize: "0.875rem",
                              lineHeight: 1.4,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {renderValue(column, row[column.id])}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {Array.isArray(actions) && actions.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />

                    {renderActions(actions, rowKey, {
                      fullWidth: true,
                    })}
                  </>
                )}
              </Paper>
            );
          })}

          {shouldShowNoData && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No hay datos disponibles
              </Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        // ESCRITORIO Y TABLET LANDSCAPE
        <TableContainer
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            overscrollBehaviorX: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table
            stickyHeader
            size={useCompactTable ? "small" : "medium"}
            aria-label="Tabla de cuestionarios"
            sx={{
              tableLayout: useCompactTable ? "fixed" : "auto",
              minWidth: useCompactTable ? "960px" : "100%",
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  const compactWidth = getCompactColumnWidth(column.id);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align || "left"}
                      sx={{
                        width: useCompactTable ? getCompactColumnWidth(column.id) : "auto",

                        minWidth: useCompactTable ? getCompactColumnWidth(column.id) : column.id === "actions" ? 250 : column.minWidth,

                        maxWidth: useCompactTable ? getCompactColumnWidth(column.id) : "none",

                        px: useCompactTable ? 1 : 2,
                        py: useCompactTable ? 1.25 : 2,
                        backgroundColor: "grey.100",
                        color: "black",
                        fontWeight: "bold",
                        fontSize: useCompactTable ? "0.75rem" : "15px",
                        lineHeight: 1.25,
                        textAlign: column.align || "left",
                        whiteSpace: column.id === "actions" ? "nowrap" : "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {renderColumnLabel(column)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((row, rowIndex) => {
                const absoluteRowIndex = page * rowsPerPage + rowIndex;

                const rowKey = row.id || row.assignapp_questionnaire_id || absoluteRowIndex;

                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={`row-${rowKey}`}
                    sx={{
                      backgroundColor: absoluteRowIndex % 2 === 0 ? "white" : "grey.100",
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      const compactWidth = getCompactColumnWidth(column.id);

                      return (
                        <TableCell
                          key={`cell-${rowKey}-${column.id}`}
                          align={column.align || "left"}
                          sx={{
                            width: useCompactTable ? getCompactColumnWidth(column.id) : "auto",

                            minWidth: useCompactTable ? getCompactColumnWidth(column.id) : column.id === "actions" ? 250 : column.minWidth,

                            maxWidth: useCompactTable ? getCompactColumnWidth(column.id) : "none",

                            px: useCompactTable ? 1 : 2,
                            py: useCompactTable ? 1 : 2,
                            fontSize: useCompactTable ? "0.75rem" : "0.875rem",
                            lineHeight: 1.35,
                            verticalAlign: "middle",
                            overflowWrap: "anywhere",
                            wordBreak: "normal",
                          }}
                        >
                          {column.id === "actions"
                            ? renderActions(value, rowKey, {
                                stacked: useCompactTable,
                              })
                            : renderValue(column, value)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {shouldShowNoData && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!shouldShowNoData && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 25, 100]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            mt: useCardLayout ? 1.5 : 0,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            borderRadius: useCardLayout ? 2 : 0,

            "& .MuiTablePagination-toolbar": {
              minHeight: 54,
              flexWrap: {
                xs: "wrap",
                sm: "nowrap",
              },
              justifyContent: {
                xs: "center",
                sm: "flex-end",
              },
              rowGap: 0.5,
              px: {
                xs: 1,
                sm: 2,
              },
            },

            "& .MuiTablePagination-spacer": {
              display: {
                xs: "none",
                sm: "block",
              },
            },

            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              m: 0,
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            },
          }}
        />
      )}
    </Paper>
  );
};

export default TableComponent;
